/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import Icons from '../../icons/icons';
import {
  labelColors,
  sizeStyles,
  evalBadgeMinWidth
} from '../../utils/constants';

import { formatEvalLabel } from '../../lib/utils';
import { wrapWithProviders } from '../../providers/Providers';
import { CreateEvalsJobsBody, EvalResults, SubmitManualLabelParams } from '../../types/api';
import { getMergedEvalTimeline } from '../../utils/eval-timeline';
import EvalFeedback from './eval-feedback/eval-feedback';
import EvalFeedbackModal from './eval-feedback/eval-feedback-modal';
import EvalTooltip from './eval-tooltip/eval-tooltip';
import { formatDateMonthDayYearTime } from '../../utils/isoToStringDateFormat';



type EvalBadgeProps = {
  evalObj: EvalResults | undefined;
  size?: 'default' | 'compact';
  templateEnums?: string[];
  templateLoading?: boolean;
  submitManualLabel?: (params: SubmitManualLabelParams) => void;
  createEvalsJobs?: (
    queryParams: Record<string, unknown>,
    body: CreateEvalsJobsBody
  ) => void | Promise<void>;
  factName?: string;
  startTime?: string;
  endTime?: string;
  appName?: string;
  workflowName?: string;
  filterStartTime?: string | null;
  filterEndTime?: string | null;
  sendEvalPromptToKahu?: (prompt: string) => void;
};

type FeedbackStatus = 'none' | 'approved' | 'disapproved' | 'added';

const EvalBadge = ({
  evalObj,
  size = 'default',
  templateEnums,
  templateLoading,
  submitManualLabel,
  createEvalsJobs,
  factName,
  startTime,
  endTime,
  appName,
  workflowName,
  filterStartTime,
  filterEndTime,
  sendEvalPromptToKahu,
}: EvalBadgeProps) => {
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackStatus>('none');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isExplaining, setIsExplaining] = useState(false);

  const [latestEntry] = getMergedEvalTimeline(evalObj);
  const latestEntryKey = `${latestEntry?.job_id ?? ''}_${latestEntry?.eval_timestamp ?? ''}`;

  // New data arrived, or the job failed.
  useEffect(() => {
    setIsExplaining(false);
  }, [latestEntryKey]);

  useEffect(() => {
    const stop = () => setIsExplaining(false);
    window.addEventListener('evaluationFailed', stop);
    return () => window.removeEventListener('evaluationFailed', stop);
  }, []);

  const evalResult = evalObj?.eval_result;
  const approvalSeedKey = `${evalObj?.fact_id ?? ''}_${evalObj?.eval_name ?? ''}_${evalObj?.eval_timestamp ?? ''}`;

  /**
   * Determines the feedback status based on the evalObj.
   * @param evalObj - The evaluation object.
   * @returns The feedback status: 'none', 'approved', or 'disapproved'.
   */
  const getFeedbackStatus = (evalObj: EvalResults | undefined): FeedbackStatus => {
    const [latestEntry] = getMergedEvalTimeline(evalObj);

    switch (latestEntry?.userAction) {
      case 'create':
        return 'added';
      case 'approve':
        return 'approved';
      case 'edit':
        return 'disapproved';
      default:
        return 'none';
    }
  };

  useEffect(() => {
    if (evalObj) {
      setFeedbackStatus(getFeedbackStatus(evalObj));
    }
  }, [approvalSeedKey]);

  /**
   * Handles user approval for the eval pill (thumbs up action)
   * @param feedback - the user feedback
   */
  const handleUserApproval = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const startingTime = filterStartTime || startTime;
    const endingTime = filterEndTime || endTime;

    const wasAlreadyApproved = feedbackStatus === 'approved';
    setFeedbackStatus('approved');

    if (
      wasAlreadyApproved ||
      !factName ||
      !evalObj?.fact_id ||
      (!appName && !workflowName)
    ) {
      return;
    }

    const manualLabelBody = {
      result: {
        label: evalObj?.eval_name || '',
        value: evalResult?.label || '',
        explanation: evalResult?.explanation || '',
        category: 'manual' as const,
      },
    };

    submitManualLabel?.({
      traceId: evalObj?.fact_id,
      factName,
      startTime: startingTime,
      endTime: endingTime,
      appName: appName || '',
      workflowName,
      body: manualLabelBody,
      actionType: 'approve',
    });
  };

  /**
   * Handles user feedback for the eval pill by submitting a manual label. (Thumbs up action)
   * @param criteria - the eval criteria
   * @param value - the new eval value
   * @param explanation - the explanation for the new eval value
   */
  const handleUserFeedback = (criteria: string, value: string, explanation: string) => {
    if (!factName || !evalObj?.fact_id || (!appName && !workflowName)) {
      return;
    }

    const startingTime = filterStartTime || startTime;
    const endingTime = filterEndTime || endTime;

    setFeedbackStatus('disapproved');

    const manualLabelBody = {
      result: {
        label: criteria,
        value,
        explanation,
        category: 'manual' as const,
      },
    };

    submitManualLabel?.({
      traceId: evalObj?.fact_id,
      factName,
      startTime: startingTime,
      endTime: endingTime,
      appName: appName || '',
      workflowName,
      body: manualLabelBody,
      actionType: 'edit',
    });

  };

  /**
   * Sends a prompt to kahu to find out more information about the eval.
   */
  const sendKahuPrompt = () => {
    const startingTime = filterStartTime || startTime;
    const endingTime = filterEndTime || endTime;

    const prompt = `Show me the history of ${evalObj?.eval_name} evaluation for ${factName} ID ${evalObj?.fact_id} from ${formatDateMonthDayYearTime(startingTime)} to ${formatDateMonthDayYearTime(endingTime)}.`;
    sendEvalPromptToKahu?.(prompt);

  };

  /**
   * Sends an explanation request for the current evaluation.
   * @param body - the request body for the explanation request
   */
  const sendExplanationRequest = async (body: CreateEvalsJobsBody) => {
    const queryParams = {
      fact_name: factName,
      trace_id: evalObj?.fact_id,
      ...(appName ? { app_name: appName } : { workflow_name: workflowName }),
    };

     setIsExplaining(true);
     try {
       await createEvalsJobs?.(queryParams, body);
     } catch {
       setIsExplaining(false);
     }
  };

  const openFeedbackModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowFeedbackModal(true);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  const label = evalResult?.label as keyof typeof labelColors | undefined;
  const colorClass = (label && labelColors[label]) ?? labelColors.neutral;

  return (
    <div
      className={cn(
        'rounded-full border relative group',
        evalBadgeMinWidth[size],
        colorClass
      )}
    onClick={(e) => {
    e.stopPropagation();
    sendKahuPrompt();
    }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {evalResult ? (
              <p className={cn('min-w-[70%] capitalize cursor-pointer whitespace-nowrap rounded-full flex gap-1 items-center justify-center group-hover:justify-start', sizeStyles[size])}>
                {feedbackStatus === 'approved' && Icons.UserApprovedIcon('size-4.5')}
                {feedbackStatus === 'disapproved' && Icons.UserFeedbackIcon('size-4.5')}
                {feedbackStatus === 'added' && Icons.UserAddedIcon('size-4.5')}
                {formatEvalLabel(evalResult?.label)}
              </p>
            ) : null}
          </TooltipTrigger>
          <TooltipContent
            className="max-w-[750px] mt-2 rounded-lg py-4 px-5 bg-[#07181E]"
            side="bottom"
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
          >
            <EvalTooltip
              evalObj={evalObj}
              size={size}
              templateEnums={templateEnums}
              sendKahuPrompt={sendKahuPrompt} 
              sendExplanationRequest={sendExplanationRequest}
              isExplaining={isExplaining}
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <EvalFeedback
        handleUserApproval={handleUserApproval}
        approved={feedbackStatus === 'approved'}
        disapproved={feedbackStatus === 'disapproved'}
        openFeedbackModal={openFeedbackModal}
      />
      {showFeedbackModal && (
        <EvalFeedbackModal
          onClose={closeFeedbackModal}
          evalObj={evalObj}
          templateEnums={templateEnums}
          templateLoading={templateLoading}
          handleUserFeedback={handleUserFeedback}
        />
      )}
    </div>
  );
};

export default wrapWithProviders(EvalBadge);